/**
 * Who can run a bot's slash command in a space, beyond what the bot asked for. A command comes with default
 * permissions (`defaultMemberPermissions`); a space's administrators can then allow or deny it for particular roles
 * and people, and switch it off in particular channels. Bots can't change these rules, only people with Manage space.
 */
import { z } from 'zod';
import { Permission, hasPermission } from './permissions';
import { idSchema } from './validation';

export const COMMAND_OVERRIDE_LIMIT = 100;

export type CommandOverrideTarget = 'role' | 'user' | 'channel';

/** One rule for one command in one space. */
export interface CommandOverride {
  targetType: CommandOverrideTarget;
  targetId: string;
  /** Roles and people: allowed or denied. Channels can only be switched off, so `allow` is always false there. */
  allow: boolean;
}

export const commandOverrideSchema = z
  .object({ targetType: z.enum(['role', 'user', 'channel']), targetId: idSchema, allow: z.boolean() })
  .refine((o) => o.targetType !== 'channel' || !o.allow, { message: 'A channel can only be switched off for a command', path: ['allow'] });

/** The whole set of rules for one command: what is sent replaces what was there. An empty list clears them. */
export const commandOverridesSchema = z
  .object({ overrides: z.array(commandOverrideSchema).max(COMMAND_OVERRIDE_LIMIT) })
  .refine((v) => new Set(v.overrides.map((o) => `${o.targetType}:${o.targetId}`)).size === v.overrides.length, { message: 'The same role, person or channel is listed twice', path: ['overrides'] });

export interface CommandRuleContext {
  /** The command's own default: permission bits people need, or null for anyone who can use commands. */
  defaultPermissions: number | null;
  overrides: readonly CommandOverride[];
  /** The person's permissions in the space (or in the channel, when `channelId` is given). */
  perms: number;
  userId: string;
  /** The roles they hold, not counting @everyone. */
  roleIds: readonly string[];
  everyoneRoleId: string;
  /** Where the command is being run. Leave out to judge the space as a whole (for listing commands). */
  channelId?: string;
}

/**
 * Whether the person may run the command. In order:
 * 1. Administrators and the owner skip the rules below, so none can lock them out (they still need what the command's
 *    own default asks for, which for them is every permission that exists).
 * 2. A channel the command is switched off in: no.
 * 3. A rule for the person: allow or deny.
 * 4. Rules for their roles: any allow wins, otherwise any deny.
 * 5. A rule for @everyone.
 * 6. Otherwise the command's own default permissions.
 */
export function canRunCommand(c: CommandRuleContext): boolean {
  const meetsDefault = c.defaultPermissions === null || hasPermission(c.perms, c.defaultPermissions);
  // Administrators skip the space's rules, so no rule can lock them out. The command's own default still stands, so a
  // command that asks for a permission nobody can hold stays out of reach of everyone.
  if (hasPermission(c.perms, Permission.ADMINISTRATOR)) return meetsDefault;
  if (c.channelId && c.overrides.some((o) => o.targetType === 'channel' && o.targetId === c.channelId)) return false;
  const person = c.overrides.find((o) => o.targetType === 'user' && o.targetId === c.userId);
  if (person) return person.allow;
  const held = c.overrides.filter((o) => o.targetType === 'role' && o.targetId !== c.everyoneRoleId && c.roleIds.includes(o.targetId));
  if (held.some((o) => o.allow)) return true;
  if (held.length > 0) return false;
  const everyone = c.overrides.find((o) => o.targetType === 'role' && o.targetId === c.everyoneRoleId);
  if (everyone) return everyone.allow;
  return meetsDefault;
}
